// Modal de detalhe + edição de uma licitação (status/fit/observações).
// Extraído de ComercialLicitacoes.tsx em 2026-05-23 (frontend wave 3B).
// L4 (2026-06-02): refactor de UX —
//   1) Botão Excluir vai pro "extra" do header (canto superior direito,
//      destacado do Cancelar/Salvar do rodapé — risco de clique errado).
//   2) Responsavel virou Select de vendedores (era input texto livre).
//   3) Fit técnico readonly por padrão, com ajuste manual explícito.
//      Checklist mede preparação documental separadamente (12/10/2026).
//   4) Adiciona indicador de aderencia do checklist (n_atende/n_total)
//      quando existe.
//   5) Historico de analises colapsavel (era sempre expandido).
//   6) Placeholder do resumo limpo.
// 2026-09-02 (passo 3 do plano de licitações) —
//   a) O modal usa a tela: 94vw até 1280, corpo rola só o que passar da
//      viewport; colunas 15/9 a partir de `lg` (em tablet as duas empilham —
//      com 14/10 em `md` o formulário ficava com 280 px).
//   b) "Prazo para proposta" em destaque, com a MESMA régua da lista
//      (prazo.ts) e a origem no Tooltip. `data_abertura` no PNCP é o início
//      da janela, não o prazo — era o que confundia.
//   c) Card "Documentos do edital": anexos baixados (link de 10 min) e os
//      links permanentes da fonte. O link "Edital" saiu do título.
//   d) 🚨 BUG: `editando` nascia da LINHA DA LISTA, que não traz
//      `observacoes` — o campo abria vazio e Salvar gravava '' por cima do
//      que existia. Agora re-sincroniza com o detalhe assim que ele chega.
//   e) Histórico de análises legível: a análise breve era um JSON cru de KB.
import { useState, useEffect, useMemo, ReactElement } from 'react';
import axios from 'axios';
import {
  Card, Button, Input, Select, Tag, Modal, Form, Typography, Space,
  Row, Col, App as AntApp, InputNumber, Collapse, Tooltip, Statistic,
  Divider, Switch, Popconfirm, Timeline, Badge, Alert, Flex,
} from 'antd';
import { GemeasDaLicitacao } from './GemeasDaLicitacao';
import {
  LinkOutlined, RobotOutlined, DeleteOutlined, EditOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined,
  QuestionCircleOutlined, EyeOutlined, FileDoneOutlined, ClockCircleOutlined,
  WarningOutlined, FileAddOutlined, SwapOutlined, BellFilled, FileTextOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { isoToDatetimeLocal, fmtDataBr } from '../../utils/dataFmt';
import { Licitacao, OportunidadeEvento } from './types';
import { STATUS_INTERNO_CFG } from './constants';
import {
  classificarPrazoEdital, situacaoLegivel, prazoEfetivoCliente, origemPrazoCliente,
  descreverOrigemPrazo, fmtDataHoraCurta, paredeDoDatetimeLocal,
} from './prazo';
import { parseAnaliseBreve, recomendacaoUI, nivelFitUI, fitTecnicoDaAnalise } from './analise';
import { fitTecnicoUI, textoPreparacaoDocumental } from './fitTecnico';
import { ehAberta, propostaJaFoi } from './pipeline/etapas';
import { Link } from 'react-router-dom';
import { radarApi, DIAS_ANTECEDENCIA_PADRAO, textoDoPipeline } from '../meu-radar/api';
import { Se } from '../../components/Se';
import { usePerms } from '../../hooks/usePerms';

const { Text, Paragraph } = Typography;

interface Vendedor { email: string; nome: string; }

// ── Faixa do radar (21/09/2026; pessoal desde 22/09) ──────────────────────
// Só o estado e o botão. Data, destinatário e anotações se editam em Meu
// Radar, onde convivem com o calendário — duplicar o formulário aqui criaria
// duas telas para a mesma linha.
//
// 🚨 `radar` aqui é o MEU item: o servidor filtra por dono. Se outra pessoa
// acompanha este edital, isto continua vazio — e é o certo.
function FaixaRadar({ oportunidadeId, radar, onMudou }: {
  oportunidadeId: number;
  radar: Licitacao['radar'];
  // `pipeline` (05/10/2026): o 📡 de edital também põe no pipeline; a ficha
  // aberta precisa saber, senão o "Salvar" dela devolve o edital à triagem.
  onMudou: (r: Licitacao['radar'], pipeline?: { status_interno: string; responsavel_email: string | null }) => void;
}) {
  const { message } = AntApp.useApp();
  const [emCurso, setEmCurso] = useState(false);
  const noRadar = !!radar;

  const alternar = async () => {
    setEmCurso(true);
    try {
      if (noRadar && radar) {
        // Remove pelo id do ITEM, não da oportunidade: o radar é pessoal.
        await radarApi.remover(radar.id);
        onMudou(null);
        message.success('Fora do seu radar. As anotações ficam guardadas.');
      } else {
        const novo = await radarApi.acompanharLicitacao(oportunidadeId);
        onMudou(novo as unknown as Licitacao['radar'], novo.no_pipeline === 'entrou'
          ? { status_interno: novo.status_interno || 'avaliando', responsavel_email: novo.pipeline_responsavel ?? null }
          : undefined);
        message.success(`No seu radar — aviso ${DIAS_ANTECEDENCIA_PADRAO} dias antes do fim do prazo${textoDoPipeline(novo)}.`);
      }
    } catch (e: any) {
      message.error(e?.response?.data?.erro || e?.response?.data?.error || e.message);
    } finally {
      setEmCurso(false);
    }
  };

  return (
    <Alert
      type={noRadar ? 'success' : 'info'}
      showIcon icon={<span style={{ fontSize: 14 }}>📡</span>}
      style={{ marginBottom: 12 }}
      message={
        <Flex justify="space-between" align="center" gap={8} wrap="wrap">
          <span style={{ fontSize: 12 }}>
            {noRadar ? (
              <>
                <strong>No seu radar</strong> — aviso {radar!.dias_antecedencia} dia(s) antes do fim
                do prazo, para <strong>{radar!.alerta_email}</strong>
                {radar!.alerta_enviado_em && <> · já enviado em {fmtDataHoraCurta(radar!.alerta_enviado_em)}</>}
                {'. '}
                {/* Link do router, não <a href>: um href recarregaria o SPA.
                    24/09/2026: o radar mudou para Comercial › Meu Radar, e o
                    `?item=` abre direto o item deste edital. */}
                <Link to={`/comercial/radar?item=${radar!.id}`}>Abrir Meu Radar</Link> para ajustar data,
                destinatário e anotações.
              </>
            ) : (
              <>Este edital <strong>não está no seu radar</strong>. No radar ele entra no seu
                calendário e avisa por e-mail antes do fim do prazo.</>
            )}
          </span>
          <Se permissao="radar.meu.gerenciar">
            <Button size="small" loading={emCurso} onClick={alternar}
              type={noRadar ? 'default' : 'primary'} danger={noRadar}>
              {noRadar ? 'Tirar do radar' : 'Adicionar ao radar'}
            </Button>
          </Se>
        </Flex>
      }
    />
  );
}

// Cor do Tag de fit_score pelo valor (mesma escala da TelaLicitacao).
function corFit(fit?: number | null): string {
  if (fit == null) return 'default';
  if (fit >= 80) return 'green';
  if (fit >= 40) return 'gold';
  return 'red';
}

// Tag da fonte: de onde a oportunidade veio. Cor fixa por fonte para o olho
// achar rápido na lista de tags do título.
function tagFonte(fonte?: string | null): ReactElement | null {
  if (fonte === 'pncp') return <Tag color="blue" style={{ margin: 0 }}>PNCP</Tag>;
  if (fonte === 'conlicitacao') return <Tag color="purple" style={{ margin: 0 }}>ConLicitação</Tag>;
  if (fonte === 'manual') return <Tag color="orange" style={{ margin: 0 }}>Manual</Tag>;
  return null;
}

// O que o formulário edita. Montado a partir do DETALHE (que traz todas as
// colunas), nunca só da linha da lista — a lista não seleciona `observacoes`.
function montarEditando(l: Partial<Licitacao>) {
  return {
    status_interno: l.status_interno || 'novo',
    fit_score: l.fit_score?.toString() || '',
    fit_resumo: l.fit_resumo || '',
    responsavel_email: l.responsavel_email || '',
    observacoes: l.observacoes || '',
    // L17 (2026-06-03) — campos editaveis essenciais pra licitacao manual
    // (mas tb funciona pra PNCP/ConLic, com aviso de overwrite no proximo sync)
    data_abertura: l.data_abertura || '',
    // Juntada com a outra fonte (07/10/2026): o formulário parte do valor que a
    // ficha mostra — o mais atualizado entre as fontes.
    valor_estimado: (l.valor_unificado ?? l.valor_estimado)?.toString() || '',
  };
}

// Formata R$ com centavos (o valor estimado vem numeric → string do node-pg).
function fmtReais(v: unknown): string {
  const n = Number(v);
  if (v == null || Number.isNaN(n)) return '—';
  return `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
}

export function ModalLicitacao({ licitacao, onClose, onSaved, onAnalisar, onGerarProposta, onVerDecisao, onExcluir }: {
  licitacao: Licitacao; onClose: () => void; onSaved: () => void;
  onAnalisar?: (l: Licitacao) => void;
  onGerarProposta?: (l: Licitacao) => void;
  onVerDecisao?: (l: Licitacao, analises: any[]) => void;
  onExcluir?: (l: Licitacao) => void;
}) {
  const { message } = AntApp.useApp();
  // Portão do servidor (05/10/2026): salvar a ficha e "marcar como vista" são
  // de quem edita a avaliação ou opera o pipeline; avaliar, de quem opera.
  // Falha ao carregar a matriz não vira "não pode" (o servidor decide).
  const { pode, erro: permsErro } = usePerms();
  const podeEditarFicha = permsErro || pode('comercial.licitacoes.oportunidades.editar_avaliacao_interna')
    || pode('comercial.licitacoes.pipeline.operar');
  const podeAvaliar = permsErro || pode('comercial.licitacoes.pipeline.operar');
  const [detalhe, setDetalhe] = useState<any>(null);
  const [vendedores, setVendedores] = useState<Vendedor[]>([]);
  // Nasce da linha (para o formulário não piscar vazio) e é substituído pelo
  // detalhe assim que ele chega — ver o useEffect abaixo.
  const [editando, setEditando] = useState(() => montarEditando(licitacao));
  const [sobrescreverFit, setSobrescreverFit] = useState(false);
  const [salvando, setSalvando] = useState(false);
  // L14b (2026-06-03) — timeline de alteracoes
  const [eventos, setEventos] = useState<OportunidadeEvento[]>([]);
  const [ackandoTodos, setAckandoTodos] = useState(false);

  const carregarEventos = async () => {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'listar_eventos_licitacao', oportunidade_id: licitacao.id },
        { headers: { Authorization: token } });
      const d = typeof r.data?.body === 'string' ? JSON.parse(r.data.body) : r.data;
      setEventos(Array.isArray(d?.eventos) ? d.eventos : []);
    } catch { setEventos([]); }
  };

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const [rDet, rVend] = await Promise.all([
          axios.get(`${API_URL}/comercial/licitacoes?action=detalhe&id=${licitacao.id}`,
            { headers: { Authorization: token } }),
          axios.get(`${API_URL}/comercial/vendedores`,
            { headers: { Authorization: token } }).catch(() => null),
        ]);
        const data = rDet.data?.body ? JSON.parse(rDet.data.body) : rDet.data;
        setDetalhe(data);
        // 🚨 Sem esta linha o formulário fica com o que a LISTA mandou — e a
        // lista não traz `observacoes`. Salvar gravava '' por cima do texto
        // que existia no banco.
        setEditando(montarEditando(data));
        // Sem isto o Select de "Responsável" abre vazio (a chamada dos
        // vendedores é tolerante a falha de propósito — daí o `if`).
        if (rVend) {
          const v = rVend.data?.body ? JSON.parse(rVend.data.body) : rVend.data;
          setVendedores(Array.isArray(v) ? v : []);
        }
      } catch { setDetalhe(licitacao); }
      carregarEventos();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licitacao.id]);

  // L14b — marca todos os eventos pendentes como vistos
  async function ackTodos() {
    setAckandoTodos(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'ack_todos_eventos_licitacao', oportunidade_id: licitacao.id },
        { headers: { Authorization: token } });
      message.success('Alterações marcadas como vistas');
      await carregarEventos();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setAckandoTodos(false); }
  }

  // L14b — count de eventos pendentes
  const eventosPendentes = eventos.filter(e => !e.acknowledged_at && e.material);

  // L14b — icone + cor por tipo de evento
  function iconePorTipo(tipo: string) {
    if (tipo === 'suspensa') return <WarningOutlined style={{ color: '#B07500' }} />;
    if (tipo === 'reaberta') return <CheckCircleOutlined style={{ color: '#0a7f4a' }} />;
    if (tipo === 'cancelada' || tipo === 'excluida') return <CloseCircleOutlined style={{ color: '#B91626' }} />;
    if (tipo === 'situacao_mudou') return <SwapOutlined style={{ color: '#0930D0' }} />;
    if (tipo === 'valor_mudou') return <SwapOutlined style={{ color: '#B07500' }} />;
    if (tipo === 'data_abertura_mudou') return <ClockCircleOutlined style={{ color: '#B07500' }} />;
    if (tipo === 'objeto_mudou') return <EditOutlined style={{ color: '#7C3AED' }} />;
    if (tipo === 'anexo_novo') return <FileAddOutlined style={{ color: '#0a7f4a' }} />;
    if (tipo === 'anexo_substituido') return <SwapOutlined style={{ color: '#B07500' }} />;
    if (tipo === 'retriagem_disparada') return <RobotOutlined style={{ color: '#7C3AED' }} />;
    if (tipo === 'importada') return <FileAddOutlined style={{ color: '#999' }} />;
    return <ClockCircleOutlined style={{ color: '#999' }} />;
  }

  // L3+L4: aderencia do checklist (vem em detalhe.checklist se existir)
  const aderencia = useMemo(() => {
    const c = detalhe?.checklist;
    if (!c || !c.total) return null;
    return {
      atende: c.atende || 0,
      atencao: c.atencao || 0,
      falta: c.falta || 0,
      pendente: c.pendente || 0,
      total: c.total,
      prontidao: c.prontidao_score,
    };
  }, [detalhe]);

  // Qual análise o ModalDecisao vai mostrar se abrirem "Ver decisão completa":
  // ele pega a análise breve MAIS RECENTE (mesma regra, `analises` já vem
  // ORDER BY criado_em DESC). Só o card dela ganha o link — pôr o botão em
  // todo card fazia o clique na análise antiga abrir a nova sem avisar.
  const idDecisaoExibida = useMemo(() => {
    const a = (detalhe?.analises || []).find((x: any) => x.tipo === 'analise_breve' && x.conteudo);
    return a?.id ?? null;
  }, [detalhe]);

  // Dados de exibição: o detalhe traz todas as colunas; enquanto não chega
  // (ou se falhar), a linha da lista serve.
  const dados: Licitacao & Record<string, any> = detalhe || licitacao;
  const fonte = dados.fonte || licitacao.fonte;
  // O mais atualizado entre as fontes, quando a licitação veio pelas duas (07/10/2026).
  const situacao = dados.situacao_unificada
    ? situacaoLegivel(dados.situacao_unificada, dados.situacao_unificada_fonte)
    : situacaoLegivel(dados.situacao, fonte);

  // Prazo para proposta — a régua é a do backend (`prazo_efetivo`); o
  // helper só repete a ordem quando o campo não veio.
  const prazoIso = prazoEfetivoCliente(dados);
  const prazo = classificarPrazoEdital(prazoIso);
  // Proposta já enviada (05/10/2026): o prazo dela aparece como fato, sem alerta.
  const propostaFoi = ehAberta(dados.status_interno) && propostaJaFoi(dados.status_interno, dados.proposta_enviada_em);
  const origemPrazo = origemPrazoCliente(dados);
  const anexos: any[] = Array.isArray(detalhe?.anexos) ? detalhe.anexos : [];
  const podeAnalisar = !!onAnalisar && dados.status_interno !== 'descartado';

  // 🚨 `palavras_match` NÃO existe no detalhe: quem calcula é a LISTAGEM
  // (`listarLicitacoes` anota cada linha contra a config atual; `obterLicitacao`
  // devolve só as colunas + análises/anexos/checklist). Lendo só de `dados`
  // (= detalhe) as tags nunca apareciam. A linha da lista é a fonte aqui.
  const palavrasMatch: string[] = Array.isArray(dados.palavras_match) && dados.palavras_match.length
    ? dados.palavras_match
    : (Array.isArray(licitacao.palavras_match) ? licitacao.palavras_match : []);

  async function salvar() {
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'atualizar', id: licitacao.id,
          status_interno: editando.status_interno,
          // L4: fit_score so vai pro backend se o usuario marcou "sobrescrever"
          // (UI evita conflito com o calculo automatico)
          ...(sobrescreverFit ? {
            fit_score: editando.fit_score ? parseInt(editando.fit_score, 10) : null,
          } : {}),
          fit_resumo: editando.fit_resumo,
          responsavel_email: editando.responsavel_email,
          observacoes: editando.observacoes,
          // L17 (2026-06-03) — data + valor editaveis
          data_abertura: editando.data_abertura || null,
          // Só vai quando a pessoa MUDOU o valor: reenviar o que a ficha mostra
          // gravaria o valor de uma fonte por cima do da outra e contaria como
          // "mudança mais recente" sem ninguém ter mexido.
          ...(editando.valor_estimado !== montarEditando(dados).valor_estimado ? {
            valor_estimado: editando.valor_estimado ? parseFloat(editando.valor_estimado) : null,
          } : {}),
        }, { headers: { Authorization: token } });
      message.success('Salvo.');
      onSaved();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  const stCfg = STATUS_INTERNO_CFG[editando.status_interno] || STATUS_INTERNO_CFG.novo;

  // L4 fix (2026-06-02): footer vai pra prop `footer` do Modal (era inline no
  // body antes). Sem isso, em telas menores os botoes Cancelar/Salvar caem
  // fora da viewport. Com `footer` prop + styles.body com maxHeight, o
  // Modal trata o body como scroll e mantem o rodape sempre visivel.
  const rodape = !detalhe ? null : (
    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
      {onExcluir ? (
        <Popconfirm
          title="Excluir licitação?"
          description="Ação definitiva. Se há proposta vinculada, será cancelada."
          okText="Excluir" okType="danger" cancelText="Cancelar"
          onConfirm={() => onExcluir(licitacao)}>
          <Button danger icon={<DeleteOutlined />} size="small">Excluir</Button>
        </Popconfirm>
      ) : <span />}
      <Space>
        <Button onClick={onClose}>{podeEditarFicha ? 'Cancelar' : 'Fechar'}</Button>
        {podeEditarFicha && <Button type="primary" loading={salvando} onClick={salvar}>Salvar</Button>}
      </Space>
    </Space>
  );

  // Rótulo de um anexo: tipo + de onde veio (upload manual, fonte do sync).
  function origemAnexo(a: any): string {
    const md = a?.metadados || {};
    if (md.upload_manual) return md.anexado_via === 'novo_negocio_modal' ? 'anexado ao criar o negócio' : 'upload manual';
    if (md.fonte) return String(md.fonte);
    return fonte === 'pncp' ? 'baixado do PNCP' : fonte === 'conlicitacao' ? 'baixado do ConLicitação' : 'baixado';
  }

  // Links permanentes da fonte — não expiram como o presigned dos anexos.
  // No ConLicitação o `link_edital` é o documento do boletim, que o
  // fornecedor apaga 24 h depois de publicar: avisamos em vez de esconder.
  const linksFonte: { href: string; rotulo: string; aviso?: string }[] = [];
  if (dados.conlic_url) {
    linksFonte.push({
      href: dados.conlic_url,
      rotulo: fonte === 'pncp' ? 'Página do edital no PNCP' : 'Página no ConLicitação',
    });
  }
  if (dados.link_edital) {
    if (fonte === 'conlicitacao') {
      linksFonte.push({ href: dados.link_edital, rotulo: 'Documento do boletim', aviso: 'expira 24 h após a publicação' });
    } else {
      linksFonte.push({ href: dados.link_edital, rotulo: fonte === 'pncp' ? 'Sistema de origem' : 'Link do edital' });
    }
  }

  return (
    <Modal open onCancel={onClose} footer={rodape} destroyOnHidden
      // O modal usa a tela: largura pelo viewport (teto 1280 para não virar
      // linha quilométrica em ultrawide) e o corpo só rola o que passar da
      // altura. Nada de scroll aninhado dentro do corpo.
      width="94vw"
      style={{ maxWidth: 1280, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto', paddingRight: 16 } }}
      title={
        <Space size={8} wrap>
          <Text strong style={{ color: '#020D7F' }}>
            {dados.numero_edital || `Licitação #${licitacao.id}`}
          </Text>
          {tagFonte(fonte)}
          {(dados.gemeas || []).map((g: any) => <span key={g.id}>{tagFonte(g.fonte)}</span>)}
          <Tag color={stCfg.tagColor} style={{ margin: 0 }}>{stCfg.label}</Tag>
          {situacao && (
            <Tooltip title={situacao.tip}>
              <Tag color={situacao.cor} style={{ margin: 0 }}>{situacao.txt}</Tag>
            </Tooltip>
          )}
          <Tooltip title={fitTecnicoUI(dados).dica}>
            <Tag color={fitTecnicoUI(dados).tagColor} icon={<RobotOutlined />} style={{ margin: 0 }}>
              Fit técnico {fitTecnicoUI(dados).completo}
            </Tag>
          </Tooltip>
          {aderencia && (
            <Tooltip title={`${aderencia.atende} atende · ${aderencia.atencao} atenção · ${aderencia.falta} falta · ${aderencia.pendente} pendente`}>
              <Tag color="blue" icon={<FileDoneOutlined />} style={{ margin: 0 }}>
                Preparação documental {aderencia.prontidao != null ? `${aderencia.prontidao}/100` : `${aderencia.atende}/${aderencia.total}`}
              </Tag>
            </Tooltip>
          )}
        </Space>
      }
    >
      {!detalhe ? <Card loading /> : (
        <Row gutter={20}>
          {/* ─── COLUNA ESQUERDA: consulta (dados + documentos + historico) ─── */}
          {/* `lg` e não `md`: entre 768 e 991 px o modal tem ~720 px e as duas
              colunas lado a lado espremem o formulário; empilhar é melhor. */}
          <Col xs={24} lg={15}>
            {/* A mesma licitação pelas duas fontes (07/10/2026). */}
            <div style={{ marginBottom: 12 }}>
              <GemeasDaLicitacao gemeas={dados.gemeas} redirecionadaDe={dados.redirecionada_de}
                onSeparado={() => onSaved?.()} />
            </div>
            {/* L14b (2026-06-03) — alerta de alterações pendentes pra essa op */}
            {eventosPendentes.length > 0 && (
              <Alert
                type="warning" showIcon
                style={{ marginBottom: 12 }}
                icon={<BellFilled />}
                message={
                  <Flex justify="space-between" align="center" gap={8}>
                    <span>
                      <strong>{eventosPendentes.length}</strong> {eventosPendentes.length === 1 ? 'alteração pendente' : 'alterações pendentes'} de revisão
                    </span>
                    {podeEditarFicha && (
                      <Button size="small" type="primary" danger loading={ackandoTodos} onClick={ackTodos}>
                        Marcar todas como vistas
                      </Button>
                    )}
                  </Flex>
                }
              />
            )}

            {/* Radar (21/09/2026) — esta é a tela em que a pessoa lê a
                análise e decide "quero acompanhar", então a decisão fica
                aqui, e não só na linha da lista. A faixa mostra o estado e
                aponta para a aba Radar, onde ficam data, destinatário e
                anotações. */}
            <FaixaRadar
              oportunidadeId={licitacao.id}
              radar={detalhe?.radar ?? null}
              onMudou={(r, pipeline) => {
                setDetalhe((d: any) => (d ? { ...d, radar: r, ...(pipeline || {}) } : d));
                // 🚨 (revisão de 05/10/2026) O formulário da ficha guarda o
                // status e o responsável lidos na abertura, e o "Salvar" manda
                // os dois de volta: sem isto, anotar e salvar depois do 📡
                // devolvia o edital à triagem e apagava o responsável.
                if (pipeline) setEditando((e) => ({ ...e, ...pipeline, responsavel_email: pipeline.responsavel_email || '' }));
              }}
            />

            <Card size="small" title="Dados da licitação" style={{ marginBottom: 12 }}>
              <Row gutter={[12, 8]} style={{ fontSize: 12 }}>
                {/* (a) quem está licitando */}
                <Col xs={24} md={8}>
                  <Text type="secondary">Órgão</Text>
                  <div><Text strong>{dados.orgao || '—'}</Text></div>
                  {dados.numero_processo && (
                    <div style={{ marginTop: 2 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>Processo {dados.numero_processo}</Text>
                    </div>
                  )}
                  {dados.area_tecnica && (
                    <div style={{ marginTop: 4 }}>
                      <Tooltip title="Área técnica atribuída pela triagem">
                        <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>{dados.area_tecnica}</Tag>
                      </Tooltip>
                    </div>
                  )}
                </Col>
                {/* (b) onde, como e quando publicou */}
                <Col xs={12} md={8}>
                  <div>
                    <Text type="secondary">Local</Text>
                    <div>{dados.cidade ? `${dados.cidade}/` : ''}{dados.uf || '—'}</div>
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <Text type="secondary">Modalidade</Text>
                    <div>{dados.modalidade || '—'}</div>
                  </div>
                  {dados.data_documento && (
                    <div style={{ marginTop: 6 }}>
                      <Text type="secondary">Publicação</Text>
                      <div>{fmtDataBr(dados.data_documento)}</div>
                    </div>
                  )}
                </Col>
                {/* (c) o que decide: prazo e valor */}
                <Col xs={12} md={8}>
                  <Text type="secondary">Prazo para proposta</Text>
                  {propostaFoi ? (
                    <div>
                      <Text style={{ fontSize: 18, lineHeight: '22px', color: '#0a7f4a', fontWeight: 600 }}>✓ proposta enviada</Text>
                      <div style={{ fontSize: 11, color: '#666' }}>
                        {dados.proposta_enviada_em ? `em ${fmtDataHoraCurta(dados.proposta_enviada_em)} · ` : ''}prazo era {fmtDataHoraCurta(prazoIso)}
                      </div>
                    </div>
                  ) : prazo ? (
                    <Tooltip title={descreverOrigemPrazo(origemPrazo)}>
                      <div style={{ cursor: 'help' }}>
                        <Text style={{ fontSize: 18, lineHeight: '22px', color: prazo.cor, fontWeight: prazo.bold ? 700 : 600 }}>
                          {prazo.icone}{prazo.rotulo}
                        </Text>
                        <div style={{ fontSize: 11, color: '#666' }}>{fmtDataHoraCurta(prazo.data)}</div>
                      </div>
                    </Tooltip>
                  ) : (
                    <div>
                      <Text type="secondary">
                        {situacao?.semData ? situacao.semData : 'sem prazo informado'}
                      </Text>
                    </div>
                  )}
                  {/* `data_abertura` no PNCP é o INÍCIO da janela de propostas.
                      Quando o prazo é ela mesma (fallback) não repetimos. */}
                  {dados.data_abertura && origemPrazo !== 'fallback:data_abertura' && (
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        Abertura da janela: {fmtDataHoraCurta(dados.data_abertura)}
                      </Text>
                    </div>
                  )}
                  {(dados.valor_unificado ?? dados.valor_estimado) != null && (
                    <div style={{ marginTop: 6 }}>
                      <Text type="secondary">Valor estimado</Text>
                      <div>
                        <Text strong style={{ color: '#020D7F', fontSize: 13 }}>{fmtReais(dados.valor_unificado ?? dados.valor_estimado)}</Text>
                      </div>
                    </div>
                  )}
                </Col>
              </Row>
              <Divider style={{ margin: '10px 0' }} />
              <Text type="secondary" style={{ fontSize: 11 }}>Objeto</Text>
              {/* Sem maxHeight aqui: quem rola é o corpo do modal. Scroll
                  dentro de scroll era o que escondia o fim do objeto. */}
              <div style={{ marginTop: 4, padding: 8, background: '#FAFBFF', borderRadius: 4 }}>
                <Text style={{ fontSize: 11.5, whiteSpace: 'pre-wrap' }}>
                  {dados.objeto || '—'}
                </Text>
              </div>
              {palavrasMatch.length > 0 && (
                <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {palavrasMatch.map((kw: string) => (
                    <Tooltip key={kw} title="Palavra-chave da configuração que casou no objeto">
                      <Tag color="green" style={{ fontSize: 9, padding: '0 4px', margin: 0, lineHeight: '14px' }}>{kw}</Tag>
                    </Tooltip>
                  ))}
                </div>
              )}
            </Card>

            {/* Documentos do edital: o que já baixamos + links da fonte */}
            <Card size="small" title={<Space><FileTextOutlined />Documentos do edital</Space>} style={{ marginBottom: 12 }}>
              {anexos.length > 0 ? (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  {anexos.map((a: any) => (
                    <Flex key={a.id} align="center" gap={8} wrap="wrap"
                      style={{ padding: '4px 6px', background: '#FAFBFF', borderRadius: 4, fontSize: 12 }}>
                      <FileTextOutlined style={{ color: '#0930D0' }} />
                      <Text style={{ flex: 1, minWidth: 160, fontSize: 12 }} ellipsis={{ tooltip: a.nome }}>
                        {a.nome || `anexo #${a.id}`}
                      </Text>
                      {a.tipo && <Tag style={{ margin: 0, fontSize: 10 }}>{a.tipo}</Tag>}
                      <Text type="secondary" style={{ fontSize: 10 }}>{origemAnexo(a)}</Text>
                      {a.vigente === false && (
                        <Tooltip title="Foi substituído por uma versão mais nova">
                          <Tag color="default" style={{ margin: 0, fontSize: 10 }}>substituído</Tag>
                        </Tooltip>
                      )}
                      <Button size="small" icon={<DownloadOutlined />}
                        disabled={!a.download_url}
                        title={a.download_url ? 'Abre em nova aba' : 'Arquivo sem link (não está no S3)'}
                        onClick={() => window.open(a.download_url, '_blank', 'noopener')}>
                        Abrir
                      </Button>
                    </Flex>
                  ))}
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    Links válidos por 10 min; reabra o modal para renovar.
                  </Text>
                </Space>
              ) : (
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Nenhum documento baixado ainda.</Text>
                  {podeAnalisar && (
                    <Button size="small" icon={<RobotOutlined />}
                      style={{ color: '#7C3AED', borderColor: '#7C3AED' }}
                      onClick={() => onAnalisar!(licitacao)}>
                      Análise breve do edital (baixa os documentos e gera parecer, ~R$ 0,30)
                    </Button>
                  )}
                </Space>
              )}
              {linksFonte.length > 0 && (
                <>
                  <Divider style={{ margin: '8px 0' }} />
                  <Space size={[12, 4]} wrap>
                    {linksFonte.map(lk => (
                      <Space key={lk.href} size={4}>
                        <a href={lk.href} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#0930D0' }}>
                          <LinkOutlined /> {lk.rotulo} ↗
                        </a>
                        {lk.aviso && <Tag color="warning" style={{ margin: 0, fontSize: 10 }}>{lk.aviso}</Tag>}
                      </Space>
                    ))}
                  </Space>
                </>
              )}
            </Card>

            {/* L4: aderencia destacada em card proprio quando existe checklist */}
            {aderencia && (
              <Card size="small" title={<Space><FileDoneOutlined />Aderência do checklist</Space>} style={{ marginBottom: 12 }}>
                <Row gutter={8}>
                  <Col span={6}>
                    <Statistic
                      title={<><CheckCircleOutlined style={{ color: '#0a7f4a' }} /> Atende</>}
                      value={aderencia.atende} suffix={`/ ${aderencia.total}`}
                      valueStyle={{ fontSize: 16, color: '#0a7f4a' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title={<><ExclamationCircleOutlined style={{ color: '#B07500' }} /> Atenção</>}
                      value={aderencia.atencao}
                      valueStyle={{ fontSize: 16, color: '#B07500' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title={<><CloseCircleOutlined style={{ color: '#B91626' }} /> Falta</>}
                      value={aderencia.falta}
                      valueStyle={{ fontSize: 16, color: '#B91626' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title={<><QuestionCircleOutlined style={{ color: '#888' }} /> Pendente</>}
                      value={aderencia.pendente}
                      valueStyle={{ fontSize: 16, color: '#888' }}
                    />
                  </Col>
                </Row>
              </Card>
            )}

            {/* L4: historico colapsavel — economia de espaco quando nao interessa */}
            {detalhe.analises?.length > 0 && (
              <Collapse size="small" ghost
                items={[{
                  key: '1',
                  label: <Text strong style={{ color: '#020D7F', fontSize: 12 }}>
                    Histórico de análises ({detalhe.analises.length})
                  </Text>,
                  children: (
                    <Space direction="vertical" size={6} style={{ width: '100%' }}>
                      {detalhe.analises.slice(0, 10).map((a: any) => {
                        // Análise breve é um JSON de vários KB — mostramos o que
                        // interessa (recomendação, fit, resumo) e o link para a
                        // decisão completa. O resto continua cru.
                        const parsed = a.tipo === 'analise_breve' ? parseAnaliseBreve(a.conteudo) : null;
                        const rec = parsed ? recomendacaoUI(parsed.recomendacao) : null;
                        const fitTec = fitTecnicoDaAnalise(parsed);
                        const fitNivel = fitTec?.nivel ? nivelFitUI(fitTec.nivel) : null;
                        const documental = a.tipo === 'recalculo_pos_checklist' || a.tipo === 'prontidao_documental';
                        return (
                          <Card key={a.id} size="small" styles={{ body: { padding: 8 } }} style={{ background: '#FAFBFF' }}>
                            <Space size={6} wrap>
                              <Tag color={documental ? 'cyan' : a.tipo === 'analise_breve' ? 'purple' : 'default'} bordered={false}>
                                {documental ? 'preparação documental' : a.tipo === 'analise_breve' ? 'análise breve' : a.tipo}
                              </Tag>
                              {rec && <Tag color={rec.tagColor} bordered={false}>{rec.label}</Tag>}
                              {fitNivel && <Tag color={fitNivel.tagColor} bordered={false}>Fit técnico {fitNivel.label}</Tag>}
                              {a.fit_score != null && (
                                <Tag color={documental ? 'cyan' : corFit(a.fit_score)} bordered={false}>
                                  {documental ? 'Preparação' : 'Fit técnico'} {a.fit_score}/100
                                </Tag>
                              )}
                              <Text type="secondary" style={{ fontSize: 10 }}>
                                {new Date(a.criado_em).toLocaleDateString('pt-BR')} · {a.criado_por}
                              </Text>
                            </Space>
                            <div style={{ marginTop: 4 }}>
                              {a.tipo === 'recalculo_pos_checklist' && <Text type="secondary" style={{ display: 'block', fontSize: 11 }}>Registro antigo do checklist; esta nota mede preparação documental.</Text>}
                              {parsed ? (
                                <>
                                  {parsed.resumo_executivo_md && (
                                    <Paragraph style={{ fontSize: 11, whiteSpace: 'pre-wrap', marginBottom: 4 }}>
                                      {parsed.resumo_executivo_md}
                                    </Paragraph>
                                  )}
                                  {!parsed.resumo_executivo_md && fitTec?.justificativa && (
                                    <Paragraph style={{ fontSize: 11, whiteSpace: 'pre-wrap', marginBottom: 4 }}>
                                      {fitTec.justificativa}
                                    </Paragraph>
                                  )}
                                  {onVerDecisao && a.id === idDecisaoExibida && (
                                    <Button type="link" size="small" icon={<EyeOutlined />} style={{ padding: 0, fontSize: 11 }}
                                      onClick={() => onVerDecisao(licitacao, detalhe.analises)}>
                                      Ver decisão completa
                                    </Button>
                                  )}
                                </>
                              ) : (
                                <Text style={{ fontSize: 11, whiteSpace: 'pre-wrap' }}>{documental ? textoPreparacaoDocumental(a.conteudo) : a.conteudo}</Text>
                              )}
                            </div>
                          </Card>
                        );
                      })}
                    </Space>
                  ),
                }]}
              />
            )}

            {/* L14b (2026-06-03) — timeline de alterações vindas dos boletins */}
            {eventos.length > 0 && (
              <Collapse size="small" ghost defaultActiveKey={eventosPendentes.length > 0 ? ['hist'] : []}
                items={[{
                  key: 'hist',
                  label: (
                    <Text strong style={{ color: '#020D7F', fontSize: 12 }}>
                      Histórico de alterações ({eventos.length})
                      {eventosPendentes.length > 0 && (
                        <Badge count={eventosPendentes.length} style={{ backgroundColor: '#B91626', marginLeft: 8 }} />
                      )}
                    </Text>
                  ),
                  children: (
                    <Timeline
                      items={eventos.slice(0, 30).map(ev => ({
                        dot: iconePorTipo(ev.tipo),
                        color: ev.acknowledged_at ? 'gray' : (ev.material ? 'red' : 'blue'),
                        children: (
                          <div style={{ fontSize: 11 }}>
                            <Space size={4} wrap>
                              <Text strong style={{ fontSize: 11 }}>{ev.resumo || ev.tipo}</Text>
                              {ev.material && !ev.acknowledged_at && (
                                <Tag color="red" style={{ fontSize: 9, margin: 0 }}>pendente</Tag>
                              )}
                              {ev.acknowledged_at && (
                                <Tooltip title={`Marcado como visto em ${new Date(ev.acknowledged_at).toLocaleString('pt-BR')} por ${ev.acknowledged_by || '—'}`}>
                                  <Tag color="default" style={{ fontSize: 9, margin: 0 }}>✓ visto</Tag>
                                </Tooltip>
                              )}
                            </Space>
                            <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                              {new Date(ev.criado_em).toLocaleString('pt-BR')}
                              {ev.notificacao_enviada_at && ' · 📧 notificado'}
                              {ev.retriagem_disparada_at && ' · 🤖 re-triado'}
                            </div>
                          </div>
                        ),
                      }))}
                    />
                  ),
                }]}
              />
            )}
          </Col>

          {/* ─── COLUNA DIREITA: edição ─── */}
          <Col xs={24} lg={9}>
            <Card size="small" title="Avaliação interna">
              <Form layout="vertical" size="small">
                {/* Etapa do pipeline se muda NO pipeline (regras, gêmeo, motivo). Aqui só
                    a triagem: novo · em análise · descartado. */}
                <Form.Item label="Status" extra={!['novo', 'em_analise', 'descartado'].includes(editando.status_interno)
                  ? <Text type="secondary" style={{ fontSize: 10 }}>No pipeline de licitações — a etapa se muda lá.</Text> : null}>
                  <Select value={editando.status_interno} showSearch optionFilterProp="label"
                    disabled={!['novo', 'em_analise', 'descartado'].includes(editando.status_interno)}
                    onChange={(v) => setEditando({ ...editando, status_interno: v })}
                    options={Object.entries(STATUS_INTERNO_CFG)
                      .filter(([k]) => ['novo', 'em_analise', 'descartado'].includes(k) || k === editando.status_interno)
                      .map(([k, v]) => ({ label: v.label, value: k }))} />
                </Form.Item>

                {/* L17 (2026-06-03) — data + valor editaveis */}
                <Form.Item label="Data de abertura" extra={
                  fonte && fonte !== 'manual'
                    ? <Text type="secondary" style={{ fontSize: 10 }}>
                        ⚠ Esta licitação é {fonte}. Próximo sync pode sobrescrever se a fonte voltar a mandar valor antigo.
                      </Text>
                    : null
                }>
                  {/* 🚨 28/09/2026: a data vai e volta como parede de Brasília,
                      sem offset (prazo.ts). O toISOString() de antes gravava a
                      hora UTC — 10:00 digitado virava 13:00 no banco. */}
                  <Input
                    type="datetime-local"
                    value={isoToDatetimeLocal(editando.data_abertura)}
                    onChange={e => setEditando({ ...editando, data_abertura: paredeDoDatetimeLocal(e.target.value) })}
                  />
                </Form.Item>

                <Form.Item label="Valor estimado (R$)">
                  <InputNumber
                    value={editando.valor_estimado ? parseFloat(editando.valor_estimado) : undefined}
                    onChange={v => setEditando({ ...editando, valor_estimado: v != null ? String(v) : '' })}
                    style={{ width: '100%' }}
                    min={0}
                    step={1000}
                    formatter={(v) => v != null ? `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : ''}
                    parser={(v: string | undefined) => Number((v || '').replace(/\D/g, ''))}
                  />
                </Form.Item>

                {/* L4: fit_score readonly por padrao + escape hatch */}
                <Form.Item label={
                  <Space size={6}>
                    <span>Fit técnico</span>
                  </Space>
                }>
                  <Space.Compact style={{ width: '100%' }}>
                    <InputNumber
                      value={editando.fit_score ? parseInt(editando.fit_score) : undefined}
                      onChange={(v) => setEditando({ ...editando, fit_score: v != null ? String(v) : '' })}
                      min={0} max={100} disabled={!sobrescreverFit}
                      style={{ width: '100%' }}
                      addonAfter="/ 100"
                    />
                  </Space.Compact>
                  <Space size={6} style={{ marginTop: 4 }}>
                    <Switch size="small" checked={sobrescreverFit}
                      onChange={setSobrescreverFit} />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Sobrescrever fit técnico manualmente
                    </Text>
                  </Space>
                  {!sobrescreverFit && (
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: 10 }}>
                        O fit avalia o objeto. Regenerar o checklist atualiza somente a preparação documental.
                      </Text>
                    </div>
                  )}
                </Form.Item>

                <Form.Item label="Resumo da análise">
                  <Input.TextArea value={editando.fit_resumo}
                    onChange={e => setEditando({ ...editando, fit_resumo: e.target.value })}
                    rows={4}
                    placeholder="O que pesa a favor / contra: atestados, prazo, valor, exigências críticas..." />
                </Form.Item>

                {/* L4: responsavel virou Select de vendedores */}
                <Form.Item label="Responsável">
                  <Select
                    value={editando.responsavel_email || undefined}
                    onChange={(v) => setEditando({ ...editando, responsavel_email: v || '' })}
                    placeholder="Selecione um vendedor"
                    allowClear showSearch optionFilterProp="label"
                    options={vendedores.map(v => ({
                      label: `${v.nome} (${v.email})`, value: v.email,
                    }))}
                  />
                </Form.Item>
                <Form.Item label="Observações">
                  <Input.TextArea value={editando.observacoes}
                    onChange={e => setEditando({ ...editando, observacoes: e.target.value })}
                    rows={3} />
                </Form.Item>
              </Form>
            </Card>

            {/* L4: acoes IA agrupadas em card proprio */}
            <Card size="small" title="Ações IA / proposta" style={{ marginTop: 12 }}
              styles={{ body: { padding: 10 } }}>
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                {/* É a análise BREVE (Haiku, ~R$ 0,30). A profunda, com
                    cruzamento contra o acervo, fica para a próxima etapa. */}
                {podeAnalisar && licitacao.triada_em && (
                  <Button size="small" block icon={<RobotOutlined />} onClick={() => onAnalisar!(licitacao)}
                    style={{ color: '#7C3AED', borderColor: '#7C3AED' }}
                    title="Dwight baixa edital + anexos, lê com Haiku e gera parecer com recomendação (~R$ 0,30, 30-90 s)">
                    Análise breve do edital
                  </Button>
                )}
                {onVerDecisao && detalhe?.analises?.length > 0
                  && detalhe.analises.some((a: any) => a.tipo === 'analise_breve' && a.conteudo) && (
                  <Button size="small" block icon={<EyeOutlined />}
                    onClick={() => onVerDecisao(licitacao, detalhe.analises)}>
                    Ver decisão sugerida
                  </Button>
                )}
                {/* 02/10/2026: o processo vive no pipeline de licitações. Na triagem o
                    botão AVALIA (entra em Avaliando, com responsável); depois, só abre. */}
                {onGerarProposta && !['novo', 'em_analise', 'descartado'].includes(licitacao.status_interno) && (
                  <Button size="small" block type="primary" onClick={() => onGerarProposta(licitacao)}>
                    Abrir no pipeline
                  </Button>
                )}
                {onGerarProposta && podeAvaliar && ['novo', 'em_analise', 'descartado'].includes(licitacao.status_interno) && (
                  <Button size="small" block type="primary" onClick={() => onGerarProposta(licitacao)}
                    style={{ background: '#0a7f4a', borderColor: '#0a7f4a' }}>
                    Avaliar no pipeline
                  </Button>
                )}
              </Space>
            </Card>
          </Col>
        </Row>
      )}

    </Modal>
  );
}
